import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { History, ArrowDown, ArrowUp, RefreshCw } from 'lucide-react';
import { useGetMyTransactions } from '@/api/balance/balance';

interface TransactionHistoryProps {
  limit?: number;
  showViewMore?: boolean;
  onViewMore?: () => void;
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({
  limit = 5,
  showViewMore = true,
  onViewMore,
}) => {
  const {
    data: transactions,
    isLoading,
    isError,
    refetch,
    isRefetching,
  } = useGetMyTransactions({
    limit,
  });

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'DEDUCTION':
        return <ArrowDown className='h-4 w-4 text-red-600' />;
      case 'ADJUSTMENT':
        return <ArrowUp className='h-4 w-4 text-blue-600' />;
      case 'ANNUAL_RESET':
      case 'CONTRACT_ALLOCATION':
        return <RefreshCw className='h-4 w-4 text-green-600' />;
      default:
        return <History className='h-4 w-4 text-gray-600' />;
    }
  };

  const getTransactionColor = (type: string, amount: number) => {
    if (type === 'DEDUCTION' || amount < 0) return 'text-red-600';
    if (type === 'ANNUAL_RESET' || type === 'CONTRACT_ALLOCATION' || amount > 0) return 'text-green-600';
    return 'text-blue-600';
  };

  const formatAmount = (amount: number) => {
    const abs = Math.abs(amount);
    const sign = amount >= 0 ? '+' : '-';
    return `${sign}€${abs.toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <History className='h-5 w-5' />
            Recent Transactions
          </CardTitle>
          <CardDescription>Your recent balance changes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='space-y-3'>
            {[...Array(3)].map((_, i) => (
              <div key={i} className='animate-pulse flex items-center gap-3'>
                <div className='h-4 w-4 bg-gray-200 rounded'></div>
                <div className='flex-1'>
                  <div className='h-4 bg-gray-200 rounded w-3/4 mb-1'></div>
                  <div className='h-3 bg-gray-200 rounded w-1/2'></div>
                </div>
                <div className='h-4 bg-gray-200 rounded w-16'></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <History className='h-5 w-5' />
            Recent Transactions
          </CardTitle>
          <CardDescription>Error loading transaction history</CardDescription>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-red-600'>Failed to load transactions</p>
          <Button variant='outline' size='sm' onClick={() => refetch()} className='mt-2'>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const transactionList = transactions?.transactions || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <History className='h-5 w-5' />
            Recent Transactions
          </div>
          <Button variant='ghost' size='sm' onClick={() => refetch()} disabled={isRefetching}>
            <RefreshCw className={`h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
        <CardDescription>Your recent balance changes</CardDescription>
      </CardHeader>
      <CardContent>
        {transactionList.length === 0 ? (
          <div className='text-center py-6'>
            <History className='h-8 w-8 text-gray-400 mx-auto mb-2' />
            <p className='text-sm text-gray-600'>No transactions yet</p>
          </div>
        ) : (
          <div className='space-y-3'>
            {transactionList.slice(0, limit).map(transaction => (
              <div key={transaction.id} className='flex items-start gap-3 p-3 rounded-lg border'>
                <div className='mt-0.5'>{getTransactionIcon(transaction.transaction_type)}</div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center justify-between'>
                    <p className='font-medium text-sm truncate'>{transaction.description || 'Balance transaction'}</p>
                    <p
                      className={`font-semibold text-sm ${getTransactionColor(transaction.transaction_type, transaction.amount)}`}
                    >
                      {formatAmount(transaction.amount)}
                    </p>
                  </div>
                  <div className='flex items-center justify-between mt-1'>
                    <Badge variant='outline' className='text-xs'>
                      {transaction.transaction_type.replace('_', ' ').toLowerCase()}
                    </Badge>
                    <p className='text-xs text-gray-500'>
                      {new Date(transaction.created_at).toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {showViewMore && transactionList.length > limit && onViewMore && (
              <div className='pt-2'>
                <Button variant='outline' size='sm' onClick={onViewMore} className='w-full'>
                  View All Transactions ({transactions?.total_count || 0})
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
